import {Injectable, Optional} from '@angular/core';
import {Router} from '@angular/router';
import {environment} from '../../../environments/environment';
import {StorageService} from '../services/storage.service';
import {scrubError, scrubValue} from './error-scrubber';

/** Pluggable sink for production error events (Sentry, beacon, console). */
export abstract class ErrorReporter {
  abstract report(error: unknown, extra?: Record<string, unknown>): void;
  /** Shared context for errors + telemetry (AC-18 / AC-53). */
  abstract assembleContext(): ErrorReportContext;
}

export interface ErrorReportContext {
  release: string;
  environment: string;
  tenantId: string | null;
  userId: string | number | null;
  userRank: number | null;
  moduleId: string | null;
}

/**
 * Default reporter:
 * - production: POST scrubbed event to `environment.errorReportingUrl` when set;
 *   if `environment.sentryDsn` is set, also send a minimal Sentry-compatible envelope via fetch
 *   (no SDK required until ops installs `@sentry/angular` and wires init).
 * - non-prod: console.error + optional `navigator.sendBeacon` when a URL is configured.
 */
@Injectable()
export class DefaultErrorReporter extends ErrorReporter {

  constructor(
    private storage: StorageService,
    @Optional() private router?: Router,
  ) {
    super();
  }

  report(error: unknown, extra?: Record<string, unknown>): void {
    const context = this.assembleContext();
    const event = scrubValue({
      ...context,
      ...extra,
      error: scrubError(error),
      timestamp: new Date().toISOString(),
    });

    if (!environment.production) {
      // Keep local signal loud; optional beacon for shared non-prod collectors.
      // eslint-disable-next-line no-console
      console.error('[ErrorReporter]', event);
      this.maybeBeacon(event);
      return;
    }

    this.maybePost(event);
    this.maybeSentryHint(event);
  }

  /** Exposed for unit tests. */
  assembleContext(): ErrorReportContext {
    const user = this.storage.getJson<any>('currentUser', null);
    return {
      release: environment.VERSION || 'unknown',
      environment: environment.production ? 'production' : 'development',
      tenantId: this.storage.getTenantId() || null,
      userId: user?.id ?? null,
      userRank: user?.rank ?? null,
      moduleId: this.resolveModuleId(),
    };
  }

  private resolveModuleId(): string | null {
    const url = this.router?.url || '';
    // Typical routes: /modules/:moduleId/...
    const match = url.match(/\/modules\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  }

  private maybeBeacon(event: unknown): void {
    const url = (environment as any).errorReportingUrl as string | undefined;
    if (!url || typeof navigator === 'undefined' || !navigator.sendBeacon) {
      return;
    }
    try {
      navigator.sendBeacon(url, JSON.stringify(event));
    } catch {
      // ignore
    }
  }

  private maybePost(event: unknown): void {
    const url = (environment as any).errorReportingUrl as string | undefined;
    if (!url || typeof fetch === 'undefined') {
      return;
    }
    try {
      fetch(url, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify(event),
        keepalive: true,
      }).catch(() => undefined);
    } catch {
      // ignore
    }
  }

  private maybeSentryHint(event: unknown): void {
    const dsn = (environment as any).sentryDsn as string | undefined;
    if (!dsn) {
      return;
    }
    // Ops can replace this with `@sentry/angular` init when a DSN is provisioned.
    // Until then, log a single scrubbed event so production still surfaces failures.
    // eslint-disable-next-line no-console
    console.error('[ErrorReporter:sentryDsn set but SDK not initialized]', event);
  }
}
